import { useAdminApi } from '@shared/hooks/useAdminApi';
import { useUserProductsAndTenorsContext } from '@shared/contexts/UserProductsAndTenorsProvider';
import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useAsync } from 'react-use';
import { ColDef } from 'ag-grid-enterprise';
import { dateService } from '@services/DateService';

export const useLiveMidPrices = () => {
  const { apiClient } = useAdminApi();
  const { tenors, userProducts, loaded } = useUserProductsAndTenorsContext();
  const [rowData, setRowData] = useState<any[]>([]);
  const [columnsWithData, setColumnsWithData] = useState<Set<string>>(new Set());
  const gridRef = useRef<any>(null);

  const monthlyTenors = useMemo(() => tenors.filter(t => t.frequency === 'monthly'), [tenors]);
  const weeklyTenors = useMemo(() => tenors.filter(t => t.frequency === 'weekly'), [tenors]);

  const monthlyRowData = useMemo(() => {
    return rowData.filter(row => userProducts.some(product => product.symbol === row.symbol && product.tenor_frequency === 'monthly'));
  }, [rowData, monthlyTenors]);

  const weeklyRowData = useMemo(() => {
    return rowData.filter(row => userProducts.some(product => product.symbol === row.symbol && product.tenor_frequency === 'weekly'));
  }, [rowData, weeklyTenors]);

  const toolPanel = useMemo(() => {
    return {
      toolPanels: [
        {
          id: 'columns',
          toolPanel: 'agColumnsToolPanel',
          labelKey: 'Columns',
          labelDefault: 'Columns',
          iconKey: 'columns',

          toolPanelParams: {
            suppressPivotMode: true,
            suppressRowGroups: true,
            suppressColumnFilter: true,
            suppressColumnSelectAll: true,
            suppressValues: true,
          },
        },
      ],
      defaultToolPanel: 'columns',
    };
  }, []);

  const monthlyColumnDefs = useMemo((): ColDef[] => {
    const baseColumns: ColDef[] = [
      {
        headerName: 'Symbol',
        field: 'symbol',
        pinned: 'left',
        lockPosition: true,
        width: 120,
        filter: true,
      },
      {
        headerName: 'MDX Name',
        field: 'mdx_name',
        pinned: 'left',
        lockPosition: true,
        width: 120,
        filter: true,
      },
    ];

    const monthlyColumns: ColDef[] = monthlyTenors.map(monthlyTenor => ({
      headerName: monthlyTenor.display.toUpperCase(),
      field: monthlyTenor.code,
      colId: monthlyTenor.code,
      width: 100,
      cellDataType: 'number',
      valueFormatter: ({ value }) => (value?.price != null ? value?.price.toFixed(2) : ''),
      hide: !columnsWithData.has(monthlyTenor.code),
      filter: 'agNumberColumnFilter',
    }));

    return [...baseColumns, ...monthlyColumns];
  }, [monthlyTenors, columnsWithData]);

  const weeklyColumnDefs = useMemo((): ColDef[] => {
    const baseColumns: ColDef[] = [
      {
        headerName: 'Symbol',
        field: 'symbol',
        pinned: 'left',
        lockPosition: true,
        width: 120,
      },
      {
        headerName: 'MDX Name',
        field: 'mdx_name',
        pinned: 'left',
        lockPosition: true,
        width: 120,
        filter: true,
      },
    ];

    const weeklyColumns: ColDef[] = weeklyTenors.map(weeklyTenor => ({
      headerName: weeklyTenor.display.toUpperCase(),
      field: weeklyTenor.code,
      colId: weeklyTenor.code,
      width: 100,
      cellDataType: 'number',
      valueFormatter: ({ value }) => (value?.price != null ? value?.price.toFixed(2) : ''),
      hide: !columnsWithData.has(weeklyTenor.code),
      filter: 'agNumberColumnFilter',
    }));

    return [...baseColumns, ...weeklyColumns];
  }, [weeklyTenors, columnsWithData]);

  const getRowId = useCallback((params: any) => {
    return params.data.symbol;
  }, []);

  const massageRowData = useCallback(
    (rawData: any) => {
      const productPriceMap = new Map<string, any>();
      const dataColumns = new Set<string>();

      if (!loaded) return;
      Object.entries(rawData).forEach(([productSymbol, priceArray]) => {
        if (Array.isArray(priceArray)) {
          const product = userProducts.find(p => p.symbol === productSymbol);
          const mdxName = product?.mdx_name;
          const productRow: any = { symbol: productSymbol, mdx_name: mdxName };

          priceArray.forEach((item: any) => {
            const symbol = item.symbol;
            const createdAt = item.created_timestamp;

            let tenorCode = '';

            const weeklyMatch = symbol.match(/\d{2}[a-z]\d{2}\d{2}[a-z]\d{2}$/);
            if (weeklyMatch) {
              tenorCode = weeklyMatch[0];
            } else {
              if (product && product.symbol) {
                const productSymbolLength = product.symbol.length;

                const tenorPart = symbol.substring(productSymbolLength);

                const allTenorMatches = tenorPart.match(/[a-z][0-9]{1,2}/g);

                if (allTenorMatches && allTenorMatches.length > 1) {
                  tenorCode = allTenorMatches[0];
                } else if (allTenorMatches && allTenorMatches.length === 1) {
                  tenorCode = allTenorMatches[0];
                }
              }

              if (!tenorCode) {
                const tenorMatch = symbol.match(/[a-z][0-9]{1,2}$/);
                if (tenorMatch) {
                  const tenorStart = symbol.indexOf(tenorMatch[0]);
                  tenorCode = symbol.substring(tenorStart);
                } else {
                  const lastLetterIndex = symbol.search(/[a-z][0-9]/);
                  if (lastLetterIndex !== -1) {
                    tenorCode = symbol.substring(lastLetterIndex);
                  }
                }
              }
            }

            if (tenorCode) {
              productRow[tenorCode] = { price: parseFloat(item.price), created_at: createdAt };
              dataColumns.add(tenorCode);
            }
          });

          productPriceMap.set(productSymbol, productRow);
        }
      });

      setColumnsWithData(dataColumns);
      setIsDataLoaded(true);

      return Array.from(productPriceMap.values());
    },
    [userProducts, loaded]
  );

  const getLiveMidPrices = useCallback(async () => {
    const response = await apiClient?.getLiveMidPrices();
    return response;
  }, [apiClient]);

  const refreshRowData = useCallback(async () => {
    const response = await getLiveMidPrices();
    if (response) {
      const processedData = massageRowData(response);

      if (processedData) {
        setRowData(processedData);
      }
    }
  }, [getLiveMidPrices, massageRowData]);

  const getAgeInMinutes = useCallback((createdAt: string) => {
    const createdTime = new Date(createdAt).getTime();
    const currentTime = Date.now();
    return (currentTime - createdTime) / (1000 * 60);
  }, []);

  const defaultColDef: ColDef = useMemo(() => {
    return {
      cellStyle: params => {
        const createdAt = params.value?.created_at;
        if (!createdAt) return null;
        const ageInMinutes = getAgeInMinutes(createdAt);

        if (ageInMinutes < 15) {
          return {
            backgroundColor: '#94DF76',
            color: 'black',
          };
        } else if (ageInMinutes >= 15 && ageInMinutes < 45) {
          return {
            backgroundColor: '#DFD476',
            color: 'black',
          };
        } else if (ageInMinutes >= 45) {
          return {
            backgroundColor: '#E94E4E',
            color: 'white',
          };
        }

        return null;
      },

      tooltipValueGetter: params => {
        const createdAt = params.value?.created_at;
        if (!createdAt) return null;
        const ageInMinutes = getAgeInMinutes(createdAt);
        const formattedAge = dateService.parseMinutesToHoursAndMinutes(ageInMinutes);
        return `${formattedAge ? `Created ${formattedAge} ago` : `Created just now`}`;
      },
      cellRenderer: 'agAnimateShowChangeCellRenderer',
    };
  }, []);

  useAsync(async () => {
    refreshRowData();
  }, [apiClient]);

  useEffect(() => {
    const interval = setInterval(async () => {
      refreshRowData();
    }, 30000);
    return () => clearInterval(interval);
  }, [refreshRowData]);

  useEffect(() => {
    if (loaded && userProducts.length > 0) {
      refreshRowData();
    }
  }, [loaded]);

  const [isDataLoaded, setIsDataLoaded] = useState(false);

  return {
    monthlyRowData,
    weeklyRowData,
    monthlyColumnDefs,
    weeklyColumnDefs,
    getRowId,
    isDataLoaded,
    toolPanel,
    gridRef,
    refreshRowData,
    defaultColDef,
  };
};
